import { Controller } from "@hotwired/stimulus"

/**
 * Controller for Plantafel section rows (appointments, sick notes, leave requests)
 * Handles cell-based (day-level) hover detection to show "+" buttons
 *
 * For elements, the entire day is a single cell (no zone splitting like tasks).
 * - Empty cells (no element covers the date): Full-height hover, centered + button
 * - Occupied cells (element covers the date): Bottom 40px hover, + button at bottom
 *
 * Cell highlighting is controlled via CSS group-hover and JS for the highlight layer.
 */
export default class extends Controller {
  static targets = ["cellsLayer", "highlightLayer", "bottomTriggerLayer"]
  static values = {
    type: String, // 'appointments', 'sick_notes', 'leave_requests'
  }

  connect() {
    this.hoverColor = this.hasCellsLayerTarget ? this.cellsLayerTarget.dataset.hoverColor : null
  }

  /**
   * Highlights the full-height cell in the highlight layer
   * Called when hovering over cells (both empty cells in overlay and bottom trigger strip)
   */
  highlightCell(event) {
    if (!this.hasHighlightLayerTarget) return

    const cell = event.currentTarget.closest(".plantafel-cell-hover")
    const dateStr = cell?.dataset.plantafelCellDateValue

    if (!dateStr) return

    // Find the corresponding highlight cell by date
    const highlightCell = this.highlightLayerTarget.querySelector(
      `.cell-highlight[data-date="${dateStr}"]`,
    )
    if (highlightCell && this.hoverColor) {
      highlightCell.style.backgroundColor = this.hexToRgba(this.hoverColor, 0.15)
    }
  }

  /**
   * Removes highlight from the full-height cell
   */
  unhighlightCell(event) {
    if (!this.hasHighlightLayerTarget) return

    const cell = event.currentTarget.closest(".plantafel-cell-hover")
    const dateStr = cell?.dataset.plantafelCellDateValue

    if (!dateStr) return

    // Find the corresponding highlight cell by date
    const highlightCell = this.highlightLayerTarget.querySelector(
      `.cell-highlight[data-date="${dateStr}"]`,
    )
    if (highlightCell) {
      highlightCell.style.backgroundColor = ""
    }
  }

  // Convert hex color to rgba with specified opacity
  hexToRgba(hex, opacity) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (result) {
      const r = parseInt(result[1], 16)
      const g = parseInt(result[2], 16)
      const b = parseInt(result[3], 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }
    return hex
  }
}
